/*
  VOICE — recorded color commentary, one line per exchange, on the beat that earned it.

  A voice at the ringside desk that speaks when something happened worth speaking about: a 5+
  damage hit, a third straight stop, the finish. Every line is a recording; nothing here is
  synthesised, which makes this the opposite of js/sfx.js in a few load-bearing ways noted below.

  WHAT DECIDES WHETHER IT SPEAKS, AND WHICH LINE. Not damage, not the card, not anything this file
  re-derives -- the NARRATIVE PILL. choose() reads the pills the composer already built for the
  exchange (the same objects that drew the badges on screen), keeps the ones this file has a pool
  for, and takes the highest `priority`. That is deliberate and it is the rule
  tests/ui-bout-superlatives-test.js already enforces for the Bout Recap: a second implementation
  of "was that a knockout" can drift from the badge, and then the desk calls a knockout over a pill
  that says something else. Key on the label, always.

  IT ALSO SOLVES THE PACING FOR FREE. Pill priority happens to rank these exactly the way a
  commentator would -- finish 112, BALLS BUSTED! 110, the category crits 108, IRON WALL! 106, the
  damage pill 90+damage, FURY 90, DEEP FLOW 82, WHIFF 70 -- so "the biggest thing that happened"
  needs no cooldown, no per-bout counter and no second frequency table to keep in sync with the
  pills. Measured over 500 bouts through the real composer, that lands at about 3.6 lines a bout
  across ~7 exchanges: roughly one every other exchange. See deploy_handoff/voice-commentary-lines.md
  for the frequency table and for the four triggers that were measured OUT (FULL BLOCK fires 5.1
  times a bout; voicing it would take the track to ~7.3 lines and stop being commentary).

  <audio> ELEMENTS, NOT decodeAudioData, for the reason the js/sfx.js header gives at length: this
  board gets opened by double-clicking index.html, and on a file:// origin fetch() is refused. A
  media element loads its own source and is never subject to that rule.

  NO PRELOAD BURST. sfx.js fetches all thirteen of its samples at the first gesture. Doing that for
  sixty-one voice files is a 3MB request storm on page load for audio most of which a given bout
  never reaches. Elements are built on first use and cached; a ~50KB take loads comfortably inside
  the 450ms delivery delay below.

  ONE SHARED ELEMENT PER FILE, WHICH IS THE OPPOSITE OF playSample's cloneNode -- and for the
  opposite reason. sfx.js clones because rewinding a shared element cuts the previous play off
  mid-sound, which is wrong when two cards are picked in quick succession. Here cutting the
  previous line off IS the requirement: two commentators talking over each other is worse than one
  interrupted.

  EVERY PATH BELOW IS A COMPLETE STRING LITERAL, and that is not a style preference.
  tools/build-dist.js derives its publish set by scraping complete 'assets/...' literals out of
  script source (referencedByScripts) -- a built-up path like 'assets/audio/voice/' + name
  contributes NOTHING and the files silently never ship. That exact gap already put out a deployed
  build with no interface sounds at all. tests/voice-lines-test.js pins the rule.
*/
(function () {
  'use strict';

  // How long after the beat renders the line begins. The impact sample fires at t0 and the two
  // would talk over each other; 450ms also puts the line inside the gap the pre-prose hold already
  // builds (BEAT_TIMING.catastrophic.prose is 420ms in ui-resolution.js), so it lands between the
  // number and the sentence rather than on top of either.
  var DELAY_MS = 450;

  // Under Fast Resolve only this tier speaks: the superlatives (106+) and the finish. The quiet-tier
  // lines were recorded at 1.3-1.7s and a quiet beat's dwell is 900ms, so they would ring across two
  // beat boundaries -- and skimming is what Fast Resolve is FOR. The desk only interjects for the
  // big stuff. Read by ui-resolution.js at fire time (Fast Resolve is togglable mid-exchange), and
  // exported rather than duplicated there.
  var FAST_RESOLVE_MIN_PRIORITY = 106;

  // 0.675, which is three quarters of the 0.9 this shipped at. Set by ear against the fight
  // samples rather than derived: commentary at 0.9 sat on top of the hit it was describing instead
  // of behind it, and the desk should be the quieter of the two. There is no volume control in the
  // interface -- one sound bit, on or off (#sfx-toggle) -- so this constant IS the mix, and the
  // only way to change it is to change it here. A slider was scoped and deferred: the brand bar
  // has no room for one inline (that toggle is 17-22px with an out-of-flow hit region just to
  // reach the 44px floor), so it would have to be a popover off the speaker, which costs the
  // one-tap mute. Worth revisiting if anyone asks for it twice.
  //
  // NOT COMPARABLE TO sfx.js's 0.55 master gain. That one is a Web Audio gain node on the
  // SYNTHESISED cues; these are <audio> elements, which cannot be routed through it (see the
  // supplied-samples note in that file) and carry their own volume instead. The fight SAMPLES are
  // 0.55-0.9 in SAMPLE_VOL, which is the range this actually sits against.
  var DEFAULT_VOL = 0.675;
  // Per-file overrides, empty on purpose. Unlike the fight samples -- recorded at wildly different
  // levels and needing a volume each -- these takes arrived at a consistent level. Add an entry
  // only for a take that turns out to need one, and keep DEFAULT_VOL as the thing that moves the
  // whole desk up or down.
  var VOICE_VOL = {};

  // ---------------------------------------------------------------- the pools
  //
  // KEYS ARE THE EXACT PILL LABEL STRINGS from js/narrative-pills.js, copied rather than retyped:
  // WHIFF uses an ASCII hyphen, FURY and DEEP FLOW use em-dashes (U+2014). A retyped key mismatches
  // silently and the line simply never plays. tests/voice-lines-test.js asserts every key here is a
  // label buildPresentation actually produces, so a reworded pill fails a suite instead of going
  // quiet.
  //
  // 'multi-strike' is the one key that is NOT a label: a flurry is a `damage` pill carrying
  // data.multiStrike, and the contract mints no dedicated pill for it. keyForPill() maps it.
  //
  // Pool depth is proportional to how often the player hears it, which is a much wider spread than
  // it looks -- IT'S OVER! fires in three of every four bouts, TIMBER! in one of forty. The
  // measured table is in deploy_handoff/voice-commentary-lines.md.
  var VOICE_LINES = {
    // -- the finish, priority 112 -----------------------------------------------------------
    "IT'S OVER!": [
      'assets/audio/voice/and-thats-the-fight.m4a',
      'assets/audio/voice/its-over.m4a',
      'assets/audio/voice/right-there.m4a',
      'assets/audio/voice/done-thats-the-finish.m4a',
      'assets/audio/voice/not-getting-up-from-that.m4a',
      'assets/audio/voice/thats-the-one-that-did-it.m4a',
      'assets/audio/voice/fights-over.m4a'
    ],
    'NOTHING LEFT!': [
      'assets/audio/voice/tanks-empty.m4a',
      'assets/audio/voice/thats-it.m4a',
      'assets/audio/voice/nothing-left-to-give.m4a'
    ],
    'BOTH DOWN!': [
      'assets/audio/voice/both-done.m4a',
      'assets/audio/voice/both-done-2.m4a',
      'assets/audio/voice/nobodys-winning-that-one.m4a',
      'assets/audio/voice/nobodys-winning-that-one-2.m4a'
    ],

    // -- the dirty crit, priority 110 -------------------------------------------------------
    'BALLS BUSTED!': [
      'assets/audio/voice/oh-low-very-low.m4a',
      'assets/audio/voice/not-clean-still-counts.m4a',
      // Two shouts of the pill's own label, delivered on top of the scripted pair. nutcracker-call
      // is NOT assets/audio/nutcracker.m4a -- that one is the groin impact sound. Named apart so no
      // grep ever confuses the two.
      'assets/audio/voice/balls-busted.m4a',
      'assets/audio/voice/nutcracker-call.m4a'
    ],

    // -- the category crits, priority 108 ---------------------------------------------------
    'BONE CRUSHER!': [
      'assets/audio/voice/you-could-hear-that-one.m4a',
      'assets/audio/voice/that-kick-landed-clean.m4a',
      // The one trimmed take: delivered as three readings in a 9-second file, cut to the first.
      // See tools/trim-voice-take.js, which records the segment map and the cut points.
      'assets/audio/voice/hurt-tomorrow.wav',
      'assets/audio/voice/shin-like-a-baseball-bat.m4a',
      'assets/audio/voice/nothing-polite-about-that.m4a'
    ],
    'LIGHTS OUT!': [
      'assets/audio/voice/thats-the-one.m4a',
      'assets/audio/voice/ooh-thats-the-one.m4a',
      'assets/audio/voice/right-on-the-button.m4a',
      'assets/audio/voice/punch-had-everything.m4a',
      'assets/audio/voice/where-did-that-come-from.m4a'
    ],
    'OVERPOWERED!': [
      'assets/audio/voice/walked-straight-through.m4a',
      'assets/audio/voice/no-answer-to-that.m4a',
      'assets/audio/voice/overwhelmed-completely.m4a',
      'assets/audio/voice/nowhere-to-go.m4a'
    ],
    'SPLIT WIDE OPEN!': [
      'assets/audio/voice/short-nasty-perfect.m4a',
      'assets/audio/voice/elbow-did-real-damage.m4a'
    ],
    'SLAMMED DOWN!': [
      'assets/audio/voice/picked-up-and-put-down.m4a',
      'assets/audio/voice/floor-did-half-of-that.m4a'
    ],
    'FULL SEND!': [
      'assets/audio/voice/all-the-way-over.m4a',
      'assets/audio/voice/everything-went-into-that.m4a'
    ],
    // A POOL OF ONE, on purpose. The two scripted TIMBER! lines were delivered as a single
    // continuous read with no silence between them, so they cannot be split cleanly and ship as one
    // take. TIMBER! fires about once in forty bouts, so a pool of one is livable; if it ever grates
    // the fix is a re-record of the second line, not an edit of this one.
    'TIMBER!': [
      'assets/audio/voice/legs-just-went.m4a'
    ],
    'STRUCTURE FAILURE!': [
      'assets/audio/voice/right-up-the-middle.m4a',
      'assets/audio/voice/knee-found-everything.m4a'
    ],
    'DEVASTATING!': [
      'assets/audio/voice/dont-know-what-to-call-that.m4a',
      'assets/audio/voice/whatever-that-was-it-worked.m4a',
      'assets/audio/voice/whatever-that-was.m4a'
    ],

    // -- earned defence, priority 106 -------------------------------------------------------
    'IRON WALL!': [
      'assets/audio/voice/three-in-a-row.m4a',
      'assets/audio/voice/guard-is-a-locked-door.m4a',
      'assets/audio/voice/coming-straight-back.m4a',
      'assets/audio/voice/cannot-find-a-way-in.m4a',
      'assets/audio/voice/three-straight.m4a',
      'assets/audio/voice/thats-a-wall.m4a'
    ],

    // -- below the Fast Resolve gate --------------------------------------------------------
    // A flurry: the damage pill with data.multiStrike, priority 90+damage.
    'multi-strike': [
      'assets/audio/voice/its-a-flurry.m4a',
      'assets/audio/voice/one-after-another.m4a'
    ],
    'FURY — +1 POWER': [
      'assets/audio/voice/another-gear.m4a',
      'assets/audio/voice/the-pressures-on.m4a',
      'assets/audio/voice/theres-the-temper.m4a'
    ],
    'DEEP FLOW — RECOVER 1 IF UNHIT': [
      'assets/audio/voice/calm-as-you-like.m4a',
      'assets/audio/voice/settled.m4a',
      'assets/audio/voice/dangerous.m4a'
    ],
    'WHIFF - 0 DAMAGE': [
      'assets/audio/voice/swing-and-nothing.m4a',
      'assets/audio/voice/miles-away.m4a',
      'assets/audio/voice/nothing-but-air.m4a'
    ]
  };

  // ---------------------------------------------------------------- selection

  // A pill to a pool key, or null for the great majority of pills that have no line.
  function keyForPill(pill) {
    if (!pill) return null;
    if (Object.prototype.hasOwnProperty.call(VOICE_LINES, pill.label)) return pill.label;
    if (pill.kind === 'damage' && pill.data && pill.data.multiStrike) return 'multi-strike';
    return null;
  }

  // The one line this exchange gets, or null. Highest priority wins; ties keep the earlier pill,
  // which is composition order and therefore resolution order.
  function choose(presentation) {
    var P = window.NarrativePillContractV24;
    if (!P || !P.flattenPills || !presentation) return null;
    var best = null;
    P.flattenPills(presentation).forEach(function (pill) {
      var key = keyForPill(pill);
      if (!key) return;
      var priority = typeof pill.priority === 'number' ? pill.priority : 0;
      if (best && priority <= best.priority) return;
      best = {
        key: key,
        label: pill.label,
        priority: priority,
        // Which beat says it: the beat holding the event this pill was built from. Null only for a
        // pill the contract minted without a source, which ui-resolution treats as "the last beat".
        sourceEventId: pill.sourceEventId || null
      };
    });
    return best;
  }

  // Which take, deterministically. The same FNV-1a device superlativeFinishLine uses in ui.js, and
  // for the same reasons: reproducible under a replayed seed, and it touches neither the ENGINE nor
  // the AUTOPLAY stream, so no fixture has to know this file exists. Varies across exchanges rather
  // than within one -- an exchange speaks once, so there is nothing to vary within it.
  function pick(key, die) {
    var pool = VOICE_LINES[key];
    if (!pool || !pool.length) return null;
    var source = String(die == null ? '' : die) + '|' + key;
    var h = 2166136261;
    for (var i = 0; i < source.length; i++) { h ^= source.charCodeAt(i); h = Math.imul(h, 16777619); }
    return pool[(h >>> 0) % pool.length];
  }

  // ---------------------------------------------------------------- playback

  var elements = {};      // src -> the one element for that file
  var failed = {};        // src -> true once its load has actually errored
  var pendingTimer = null;
  var playing = null;
  // Dedupe against a re-render of the same beat. A single token, not a set: only one line fires per
  // exchange by construction, so there is never a second live token -- and a set here would grow
  // for the length of the session with nothing ever removing an entry.
  var lastToken = null;

  // ONE BIT OF SOUND STATE, shared with sfx.js. Asked through SFX where it exists; sfx.js also
  // publishes the same bit onto the document element, which is the answer when audio has been
  // removed by deleting that file but this one is still loaded.
  function isMuted() {
    if (window.SFX && typeof window.SFX.isMuted === 'function') return !!window.SFX.isMuted();
    try { return document.documentElement.getAttribute('data-sfx') === 'off'; } catch (e) { return false; }
  }

  function elementFor(src) {
    if (elements[src]) return elements[src];
    try {
      var a = new Audio(src);
      a.preload = 'auto';
      a.volume = VOICE_VOL[src] != null ? VOICE_VOL[src] : DEFAULT_VOL;
      a.addEventListener('error', function () { failed[src] = true; });
      elements[src] = a;
      return a;
    } catch (e) { return null; }        // jsdom and anything without HTMLAudioElement
  }

  function stopPlaying() {
    if (!playing) return;
    try { playing.pause(); } catch (e) { /* nothing to do about it */ }
    playing = null;
  }

  function startFile(src) {
    var a = elementFor(src);
    if (!a || failed[src] || a.error) return false;
    stopPlaying();                       // a new line always cuts the one before it
    // Only seek when there is somewhere to seek back FROM. Setting currentTime before any data has
    // loaded throws in some browsers, and an untouched element already starts at zero.
    try { if (a.currentTime) a.currentTime = 0; } catch (e) { /* plays from the top regardless */ }
    var p;
    try { p = a.play(); } catch (e) { return false; }
    // A rejection is NOT remembered, which is the deliberate difference from playSample in sfx.js.
    // There, a rejected play blacklists the sample and the synthesised cue takes over. Here there is
    // no fallback, so blacklisting on the browser's pre-gesture refusal would silence that one line
    // for the rest of the session over a condition that has already passed. Only a real load error
    // (the listener in elementFor) marks a file dead.
    if (p && p.catch) p.catch(function () { /* refused or undecodable: silence is the answer */ });
    playing = a;
    return true;
  }

  // The cue-bus handler. detail: { key, die, token }.
  function speak(detail) {
    var d = detail || {};
    if (!d.key || !VOICE_LINES[d.key]) return false;
    // Mute is checked BEFORE the token is consumed: a muted line never spoke, so unmuting and
    // re-rendering the beat should still be able to say it.
    if (isMuted()) return false;
    if (d.token != null) {
      if (lastToken === d.token) return false;
      lastToken = d.token;
    }
    var src = pick(d.key, d.die);
    if (!src) return false;
    clearTimeout(pendingTimer);
    pendingTimer = setTimeout(function () { pendingTimer = null; startFile(src); }, DELAY_MS);
    return true;
  }

  // Silence now: the player advanced by hand, and commentary carrying over onto the beat they chose
  // to see is noise. Deliberately NOT called on an auto-advance -- clipping a word off the end of
  // every beat under Fast Resolve sounds broken rather than tight.
  function cut() {
    clearTimeout(pendingTimer);
    pendingTimer = null;
    stopPlaying();
  }

  // ---------------------------------------------------------------- wiring

  window.VOICE = {
    // The bus cues this file handles, declared the way SFX.CUES is. tests/sfx-test.js audits
    // UISound.CUES against the UNION of the two, so a cue declared on the bus and handled by
    // nobody still fails -- which is the whole point of that audit -- while 'voice' being
    // deliberately unsynthesised is no longer a false positive.
    CUES: ['voice'],
    LINES: VOICE_LINES,
    FAST_RESOLVE_MIN_PRIORITY: FAST_RESOLVE_MIN_PRIORITY,
    DELAY_MS: DELAY_MS,
    hasLine: function (key) { return !!(key && VOICE_LINES[key] && VOICE_LINES[key].length); },
    choose: choose,
    keyForPill: keyForPill,
    play: speak,
    cut: cut,
    // Exported so the suites can assert the chooser's membership and determinism against the real
    // pools rather than a copy -- the same reason ui.js exports FINISHER_LINES.
    _pick: pick,
    _isMuted: isMuted
  };

  function boot() {
    // Through the cue bus, exactly like sfx.js, so the call site in ui-resolution.js stays a
    // UISound.play() and commentary remains removable by deleting this file. The 'advance' cue is
    // NOT claimed here: UISound.on holds one handler per cue and sfx.js already owns that one, so
    // the interrupt runs as a direct VOICE.cut() from ui-resolution instead. Cutting a line is
    // presentation pacing, not a sound cue, and it does not belong on the bus.
    if (window.UISound && window.UISound.on) {
      window.UISound.on('voice', function (name, detail) { speak(detail); });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
